import type { AccessibilityPreferences } from '@molt/shared';
import type { MenuSection } from '@/components/Menu';
import { sourceInterfaceMessage } from '@/lib/keyboard-shortcuts';
import type { InterfaceLanguageContextValue } from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface AccessibilityMenuState {
  preferences: AccessibilityPreferences | null;
  loading?: boolean;
  singleKeyPending?: boolean;
  reducedMotionPending?: boolean;
  t?: InterfaceTranslator | undefined;
  onOpenShortcuts: () => void;
  onSingleKeyShortcutsDisabledChange: (disabled: boolean) => void;
  onReducedMotionOverrideChange: (enabled: boolean) => void;
}

export function accessibilityMenuSections(state: AccessibilityMenuState): MenuSection[] {
  const preferences = state.preferences ?? {
    singleKeyShortcutsDisabled: false,
    reducedMotionOverride: false,
  };
  const singleKeyDisabled = state.loading === true || state.singleKeyPending === true;
  const reducedMotionDisabled =
    state.loading === true || state.reducedMotionPending === true;
  const t = state.t ?? sourceInterfaceMessage;

  return [
    {
      id: 'accessibility',
      heading: t('accessibility.heading'),
      items: [
        {
          id: 'keyboard-shortcuts',
          label: t('accessibility.keyboardShortcuts'),
          shortcut: '?',
          onSelect: state.onOpenShortcuts,
        },
        {
          id: 'single-key-shortcuts',
          label: preferences.singleKeyShortcutsDisabled
            ? t('accessibility.singleKeyOff')
            : t('accessibility.singleKeyOn'),
          disabled: singleKeyDisabled,
          keepOpenOnSelect: true,
          onSelect: () =>
            state.onSingleKeyShortcutsDisabledChange(
              !preferences.singleKeyShortcutsDisabled,
            ),
        },
        {
          id: 'reduced-motion',
          label: preferences.reducedMotionOverride
            ? t('accessibility.reducedMotionOn')
            : t('accessibility.reducedMotionOff'),
          disabled: reducedMotionDisabled,
          keepOpenOnSelect: true,
          onSelect: () =>
            state.onReducedMotionOverrideChange(!preferences.reducedMotionOverride),
        },
      ],
    },
  ];
}
